import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../features/auth/authSlice';
import { getDashboardStats } from '../features/contentCompliance/contentComplianceSlice';
import { Plus, BarChart3, FileText, Shield, CheckCircle2 } from 'lucide-react';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import RecentActivity from './RecentActivity';
import { track } from '../analytics/analytics';
import { CREDIT_PACKS } from '../features/billing/constants';
import './DashboardPage.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

function DashboardPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const dashboardStats = useSelector((state) => state.contentCompliance.dashboardStats);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showTopupBanner, setShowTopupBanner] = useState(false);

  // Fetch dashboard stats on mount
  useEffect(() => {
    if (user) {
      dispatch(getDashboardStats());
    }
  }, [dispatch, user]);

  // Returning from Stripe Checkout: success_url is /dashboard?topup_session=...
  // The webhook adds the credits asynchronously, so we show a "finalizing"
  // banner and re-fetch stats so the new balance appears once it lands.
  useEffect(() => {
    const sessionId = searchParams.get('topup_session');
    if (!sessionId) return;
    // Single pack today. transaction_id lets GA drop duplicates (e.g. a
    // back-button return). Misses buyers who close the tab after paying --
    // Stripe stays the source of truth for revenue.
    const pack = CREDIT_PACKS[0];
    track('purchase', {
      transaction_id: sessionId,
      currency: 'USD',
      value: pack.priceCents / 100,
      items: [{ item_id: pack.id, item_name: `${pack.credits} credits`, price: pack.priceCents / 100, quantity: 1 }],
    });
    setShowTopupBanner(true);
    dispatch(getDashboardStats());
    // Drop the query param so a refresh doesn't re-trigger the banner.
    searchParams.delete('topup_session');
    setSearchParams(searchParams, { replace: true });
    const t = setTimeout(() => setShowTopupBanner(false), 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Stats from API
  const stats = {
    totalChecks: dashboardStats?.total_checks ?? 0,
    savedVideos: dashboardStats?.saved_content ?? 0,
    averageScore: dashboardStats?.average_score ?? null
  };

  // Platform stats from API
  const platformStats = dashboardStats?.platforms ?? {};

  // Platform config
  const platforms = [
    { key: 'youtube', name: 'YouTube', icon: youtubeIcon },
    { key: 'tiktok', name: 'TikTok', icon: tiktokIcon },
    { key: 'instagram', name: 'Instagram', icon: instagramIcon },
    { key: 'facebook', name: 'Facebook', icon: facebookIcon }
  ];

  // Get user's first name for greeting
  const getFirstName = () => {
    if (!user?.name) return 'there';
    return user.name.split(' ')[0];
  };

  // Check if stat value is empty
  const isStatEmpty = (value) => {
    return value === null || value === undefined || value === 0;
  };

  // Format stat value - show dash for empty/null values
  const formatStatValue = (value, suffix = '') => {
    if (isStatEmpty(value)) return '—';
    return `${value}${suffix}`;
  };

  const handleStartNewScan = () => {
    navigate('/dashboard/new-check');
  };

  // Logged-in view
  return (
    <div className="dashboard-page">
      {showTopupBanner && (
        <div className="dashboard-topup-banner" role="status">
          <CheckCircle2 size={18} className="dashboard-topup-icon" aria-hidden="true" />
          <span>Payment received — your credits will appear here in a moment.</span>
        </div>
      )}
      {/* Welcome Section */}
      <section className="dashboard-welcome">
        <div className="dashboard-welcome-header">
          <div className="dashboard-welcome-text">
            <h2 className="dashboard-welcome-title">Welcome back, {getFirstName()}!</h2>
            <p className="dashboard-welcome-description">
              Check your content against platform guidelines and ensure your videos are ready for publishing.
            </p>
          </div>
          <button className="dashboard-start-scan-btn" onClick={handleStartNewScan}>
            <Plus size={20} />
            Check Content
          </button>
        </div>
      </section>

      {/* Summary Statistics */}
      <section className="dashboard-stats">
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon purple">
            <BarChart3 size={20} />
          </div>
          <div className="dashboard-stat-content">
            <p className="dashboard-stat-label">Total Checks</p>
            <p className={`dashboard-stat-value ${isStatEmpty(stats.totalChecks) ? 'empty' : ''}`}>
              {formatStatValue(stats.totalChecks)}
            </p>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon purple">
            <FileText size={20} />
          </div>
          <div className="dashboard-stat-content">
            <p className="dashboard-stat-label">Saved Videos</p>
            <p className={`dashboard-stat-value ${isStatEmpty(stats.savedVideos) ? 'empty' : ''}`}>
              {formatStatValue(stats.savedVideos)}
            </p>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon yellow">
            <Shield size={20} />
          </div>
          <div className="dashboard-stat-content">
            <p className="dashboard-stat-label">Average Score</p>
            <p className={`dashboard-stat-value ${isStatEmpty(stats.averageScore) ? 'empty' : ''}`}>
              {formatStatValue(stats.averageScore, '%')}
            </p>
          </div>
        </div>
      </section>

      {/* Platform Breakdown */}
      <section className="dashboard-platforms">
        <h3 className="dashboard-section-title">Platform Breakdown</h3>
        <div className="dashboard-platform-cards">
          {platforms.map((platform) => {
            const platformData = platformStats[platform.key] || {};
            const count = platformData.count ?? 0;
            const avgScore = platformData.average_score ?? null;

            return (
              <div key={platform.key} className="dashboard-platform-card">
                <div className="dashboard-platform-header">
                  <img
                    src={platform.icon}
                    alt={platform.name}
                    className="dashboard-platform-icon"
                  />
                  <span className="dashboard-platform-name">{platform.name}</span>
                </div>
                <div className="dashboard-platform-stats">
                  <div className="dashboard-platform-stat">
                    <span className="dashboard-platform-stat-value">
                      {count > 0 ? count : '0'}
                    </span>
                    <span className="dashboard-platform-stat-label">Scans</span>
                  </div>
                  <div className="dashboard-platform-stat">
                    <span className={`dashboard-platform-stat-value ${avgScore !== null ? (avgScore >= 80 ? 'good' : avgScore >= 60 ? 'warning' : 'poor') : ''}`}>
                      {avgScore !== null ? `${avgScore}%` : '—'}
                    </span>
                    <span className="dashboard-platform-stat-label">Average Score</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent Activity */}
      <RecentActivity limit={5} onStartNewScan={handleStartNewScan} />
    </div>
  );
}

export default DashboardPage;
